import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * @typedef {object} VariantPriceOptions
 * @property {boolean} [showSalePriceFirst]
 * @property {string} [textRegular]
 * @property {string} [textSale]
 */

/**
 * @param {{ price?: number, price_formatted?: string, compare_at_price?: number | null, compare_at_price_formatted?: string } | null | undefined} variant
 * @param {VariantPriceOptions} [options]
 * @returns {string}
 */
export function renderVariantPriceHTML(variant, options = {}) {
  if (!variant) return '';

  const price = variant.price_formatted || '';
  const compare = variant.compare_at_price_formatted || '';
  const showCompare = Number(variant.compare_at_price) > Number(variant.price);
  const showSalePriceFirst = options.showSalePriceFirst === true;
  const textRegular = options.textRegular || '';
  const textSale = options.textSale || '';

  const compareGroup = showCompare
    ? `<span role="group"><span class="visually-hidden">${textRegular}&nbsp;</span><span class="compare-at-price">${compare}</span></span>`
    : '';
  const priceMarkup = showCompare
    ? `<span role="group"><span class="visually-hidden">${textSale}&nbsp;</span><span class="price">${price}</span></span>`
    : `<span class="price">${price || '&nbsp;'}</span>`;

  return showSalePriceFirst ? `${priceMarkup}${compareGroup}` : `${compareGroup}${priceMarkup}`;
}

/**
 * @param {HTMLElement | null | undefined} container
 * @param {{ price?: number, price_formatted?: string, compare_at_price?: number | null, compare_at_price_formatted?: string } | null | undefined} variant
 * @param {VariantPriceOptions} [options]
 */
export function applyVariantPrice(container, variant, options = {}) {
  if (!container) return;

  const unitPrice = container.querySelector('.unit-price');
  const unitPriceClone = unitPrice ? unitPrice.cloneNode(true) : null;

  container.innerHTML = renderVariantPriceHTML(variant, options);

  if (unitPriceClone instanceof HTMLElement) {
    container.append(unitPriceClone);
  }
}

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const html = event.detail.data.html;
    if (!html) {
      this.#updatePriceFromVariant(event.detail.resource);
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };

  /**
   * @param {{
   *   id?: string|number,
   *   price?: number,
   *   price_formatted?: string,
   *   compare_at_price?: number | null,
   *   compare_at_price_formatted?: string
   * } | null} variant
   */
  #updatePriceFromVariant(variant) {
    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    applyVariantPrice(priceContainer, variant, {
      showSalePriceFirst: this.dataset.showSalePriceFirst === 'true',
      textRegular: this.dataset.textPriceRegular || '',
      textSale: this.dataset.textPriceSale || '',
    });

    const installmentIdInput = this.querySelector('.installments input[name="id"]');
    if (installmentIdInput instanceof HTMLInputElement) {
      installmentIdInput.value = variant?.id != null ? String(variant.id) : '';
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
